import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ViewIcon from '@mui/icons-material/Visibility'
import { supabase } from '../config/supabase'
import Layout from '../components/Layout'
import MobileFamilyCard from '../components/MobileFamilyCard'
import FamilyDetailsDialog from '../components/FamilyDetailsDialog'

const FamilyList = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchTerm, setSearchTerm] = useState('')
  const [families, setFamilies] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedFamily, setSelectedFamily] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [editingFamily, setEditingFamily] = useState(null)
  const [familyName, setFamilyName] = useState('')

  useEffect(() => {
    console.log('FamilyList mounted')
    fetchFamilies()
  }, [])

  const fetchFamilies = async () => {
    try {
      setLoading(true)
      console.log('Fetching families...', { searchTerm }) // Debug log
      
      // Build the query
      let query = supabase
        .from('families')
        .select(`
          id,
          family_name,
          contacts (
            id,
            first_name,
            last_name,
            phone,
            email
          )
        `)

      // Add search filter if searchTerm exists
      if (searchTerm && searchTerm.trim() !== '') {
        query = query.ilike('family_name', `%${searchTerm.trim()}%`)
        console.log('Applying search filter:', searchTerm.trim())
      }

      // Add ordering
      const { data, error } = await query.order('family_name')
      
      console.log('Query result:', { data, error }) // Debug log
      
      if (error) {
        console.error('Supabase error:', error)
        throw error
      }

      if (!data) {
        console.log('No data returned from query')
        setFamilies([])
        return
      }

      console.log('Setting families:', data)
      setFamilies(data)
    } catch (error) {
      console.error('Error fetching families:', error.message)
      setFamilies([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFamilies()
    }, 300) // Wait 300ms after last keystroke before searching

    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleOpenDialog = (family = null) => {
    if (family) {
      setFamilyName(family.family_name)
      setEditingFamily(family)
    } else {
      setFamilyName('')
      setEditingFamily(null)
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingFamily(null)
    setFamilyName('')
  }

  const handleSaveFamily = async () => {
    if (!familyName.trim()) {
      alert('Please enter a family name')
      return
    }

    try {
      if (editingFamily) {
        const { error } = await supabase
          .from('families')
          .update({ family_name: familyName.trim() })
          .eq('id', editingFamily.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('families')
          .insert([{ family_name: familyName.trim() }])

        if (error) throw error
      }

      handleCloseDialog()
      fetchFamilies()
    } catch (error) {
      console.error('Error saving family:', error)
      alert('Error saving family. Please try again.')
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this family? This will remove the family association from all members but will not delete the members.')) {
      try {
        // First update all contacts to remove the family association
        const { error: contactsError } = await supabase
          .from('contacts')
          .update({ family_id: null })
          .eq('family_id', id)

        if (contactsError) throw contactsError

        // Then delete the family
        const { error: familyError } = await supabase
          .from('families')
          .delete()
          .eq('id', id)

        if (familyError) throw familyError

        fetchFamilies()
      } catch (error) {
        console.error('Error deleting family:', error)
        alert('Error deleting family. Please try again.')
      }
    }
  }

  const handleViewFamily = (family) => {
    setSelectedFamily(family)
    setDetailsOpen(true)
  }

  const handleCloseDetails = () => {
    setDetailsOpen(false)
    setSelectedFamily(null)
  }

  const addTestFamily = async () => {
    try {
      const { data, error } = await supabase
        .from('families')
        .insert([
          {
            family_name: 'Test Family'
          }
        ])
        .select()

      if (error) throw error
      console.log('Test family added:', data)
      fetchFamilies()
    } catch (error) {
      console.error('Error adding test family:', error)
    }
  }

  const renderDebugButton = () => {
    if (process.env.NODE_ENV === 'development') {
      return (
        <Button
          variant="outlined"
          onClick={addTestFamily}
          sx={{ mt: 2 }}
        >
          Add Test Family
        </Button>
      )
    }
    return null
  }

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search families..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              whiteSpace: 'nowrap',
              minWidth: { xs: '100%', sm: 'auto' },
            }}
          >
            Add Family
          </Button>
        </Box>

        {renderDebugButton()}

        {isMobile ? (
          <Box>
            {loading ? (
              <Typography align="center" sx={{ py: 4 }}>Loading...</Typography>
            ) : families.length === 0 ? (
              <Typography align="center" sx={{ py: 4 }}>No families found</Typography>
            ) : (
              families.map((family) => (
                <MobileFamilyCard
                  key={family.id}
                  family={family}
                  onDelete={handleDelete}
                  onView={handleViewFamily}
                />
              ))
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Family Name</TableCell>
                  <TableCell>Members</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : families.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      No families found
                    </TableCell>
                  </TableRow>
                ) : (
                  families.map((family) => (
                    <TableRow 
                      key={family.id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="subtitle2">
                          {family.family_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {family.contacts?.length || 0} members
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewFamily(family)}
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(family)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(family.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <FamilyDetailsDialog
          open={detailsOpen}
          family={selectedFamily}
          onClose={handleCloseDetails}
        />

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
            },
          }}
        >
          <DialogTitle sx={{ 
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
            {editingFamily ? 'Edit Family' : 'Add New Family'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Family Name"
              fullWidth
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ 
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveFamily} 
              variant="contained" 
              color="primary"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  )
}

export default FamilyList
